import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  createRestaurant, getRestaurantById, getRestaurants, restaurantImagesSync, updateRestaurant, 
  updateRestaurantStatus, uploadMenuImages, uploadMenuXlsx,
} from '../../axios/restaurantRequests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  translate,
} from '../../helperFunctions/translation';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  IRestaurant, IRestaurantBranch, IRestaurantImage, IRestaurantStore,
} from '../storeInterfaces/restaurantInterface';
import { RequestStatus } from 'mobx-store';

export class RestaurantStore implements IRestaurantStore {
  @observable restaurantsList: any[] = [];

  @observable selectedRestaurant = {
  };

  @observable loading = false;

  @observable getRestaurantsRequestStatus: RequestStatus = RequestStatus.IDLE;

  private static restructureRestaurant(restaurant: IRestaurant): any {
    return {
      ...restaurant,
      images: (restaurant.restaurantImages && restaurant.restaurantImages.length
        ? restaurant.restaurantImages.map((image: IRestaurantImage) => ({
          imageUrl: image.imageURL, key: image.restaurantImageId,
        })) : []),
      availableBranches: (restaurant.branches && restaurant.branches
        ? restaurant.branches.map((branch: IRestaurantBranch) => (branch.branchId)) : []),
      logo: (restaurant.logoURL ? [{
        imageUrl: restaurant.logoURL,
      }] : []),
    };
  }

  private static restructureCreateRestaurant(restaurant: any): any {
    return {
      title: restaurant.title,
      images: restaurant.images,
      description: restaurant.description,
      availableBranchesIds: restaurant.availableBranches,
      logoURL: restaurant.logo,
      availableCountries: restaurant.availableCountries,
    };
  }

  private static restructureUpdatedRestaurant(restaurant: any): any {
    return {
      title: restaurant.title,
      newImages: restaurant.newImages,
      deletedImagesIds: restaurant.deletedImagesIds,
      description: restaurant.description,
      availableBranchesIds: restaurant.availableBranches,
      logoURL: restaurant.logo,
      availableCountries: restaurant.availableCountries,
    };
  }

  @action
  public async getRestaurants(countries: ICountry[], langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      this.getRestaurantsRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);

      const params = {
        availableCountries,
        status: 'all',
      };


      const response = await Connections.wadiDeglaAxios.httpGet(getRestaurants, {
        ...getHeader(langCode), params,
      });
      this.restaurantsList = this.restructureRestaurantsList(response.data);
      this.getRestaurantsRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getRestaurantsRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async filterRestaurants(query: string, countries: ICountry[], langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const availableCountries = convertCountriesToISOCodeString(countries);

      const config = {
        ...getHeader(langCode),
        params: {
          query, availableCountries,
        },
      };
      const response = await Connections.wadiDeglaAxios.httpGet(getRestaurants, config);
      this.restaurantsList = this.restructureRestaurantsList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getRestaurantById(id: number, langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getRestaurantById(id), getHeader(langCode));
      this.selectedRestaurant = RestaurantStore.restructureRestaurant(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createRestaurant(newRestaurant: any, langCode: string = DefaultLanguage): Promise<any> {
    return new Promise<void>(async (res, rej) => {
      try {
        const allImages = await this.uploadRestaurantImages(newRestaurant.images);
        const logo = await this.uploadSingleImage(newRestaurant.logo);
        newRestaurant.images = allImages;
        newRestaurant.logo = logo;
        const response = await Connections.wadiDeglaAxios.httpPost(createRestaurant, RestaurantStore.restructureCreateRestaurant(newRestaurant), getHeader(langCode));
        res(response.data.restaurantId);
      } catch (error) {
        rej(error);
      }
    });
  }

  @action.bound
  public async updateRestaurant(updatedRestaurant: any, id: number, initGallery: {imageUrl: string; key: number}[],
    langCode: string = DefaultLanguage): Promise<any> {
    try {
      const deletedImagesIds: number[] = [];
      const newImages: any[] = [];
      for (const image of updatedRestaurant.images) {
        if (image.preview) {
          newImages.push(image);
        } else {
          const findIndex = initGallery.findIndex((media) => media.key === image.key);
          if (findIndex !== -1) {
            initGallery.splice(findIndex, 1);
          }
        }
      }
      for (const image of initGallery) {
        deletedImagesIds.push(image.key);
      }
      const uploadedImages = await this.uploadRestaurantImages(newImages);
      const logo = await this.uploadSingleImage(updatedRestaurant.logo);
      updatedRestaurant.newImages = uploadedImages;
      updatedRestaurant.deletedImagesIds = deletedImagesIds;
      updatedRestaurant.logo = logo;
      await Connections.wadiDeglaAxios.httpPut(updateRestaurant(id), RestaurantStore.restructureUpdatedRestaurant(updatedRestaurant), getHeader(langCode));
    } catch (error) {
      console.log(error);
    }
  }

  @action.bound
  public async updateRestaurantStatus(updatedRestaurantStatus: boolean, index: number): Promise<any> {
    try {
      await Connections.wadiDeglaAxios.httpPut(updateRestaurantStatus(this.restaurantsList[index].restaurantId),
        {
          isActive: updatedRestaurantStatus,
        });
    } catch (error) {
      console.log(error);
    } finally {
      this.restaurantsList[index].isActive = updatedRestaurantStatus;
    }
  }

  @action.bound
  public async uploadMenusXlsx(restaurantId: number,file: File): Promise<void> {
    try {
      await uploadFile(uploadMenuXlsx(restaurantId), file);
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  }

  @action.bound
  public async restaurantImagesSync(): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpGet(restaurantImagesSync);
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  }

  @action.bound
  public async uploadMenuImages(restaurantId: number, file: File): Promise<void> {
    try {
      await uploadFile(uploadMenuImages(restaurantId), file);
      return Promise.resolve();
    } catch (error) {
      throw error as Error;
    }
  }

  private restructureRestaurantsList(restaurants: any[]): any[] {
    return restaurants.map((restaurant: IRestaurant) => ({
      restaurantId: restaurant.restaurantId,
      title: restaurant.title,
      isActive: restaurant.isActive,
      menu: translate('restaurantsListMenuButton', {
      }),
      edit: translate('restaurantsListEditButton', {
      }),
      bulk: translate("bulkSeed")
    }));
  }

  private uploadRestaurantImages(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allImages;
      }).catch((error) => {
        console.log('error');
        return allImages;
      });
  }

  private uploadSingleImage(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return (allImages.length ? allImages[0] : null);
      }).catch((error) => {
        console.log('error');
        return (allImages.length ? allImages[0] : null);
      });
  }
}
